/**
 * SegmentedToggle — a compact view-switcher matching the brandbook's .seg widget.
 *
 * Renders a `role="group"` container (.seg) with one button per option.
 * The active option receives `aria-pressed="true"` and the `.on` modifier
 * class. Each button may optionally display an Icon glyph before its label.
 * Used on Board, TaskList, and any screen that offers multiple view modes.
 *
 * Usage:
 *   <SegmentedToggle
 *     options={[{ value: "board", label: "Board", icon: "board" }, ...]}
 *     value={view}
 *     onChange={setView}
 *   />
 */

import { Icon } from "@/components/Icon";
import type { IconName } from "@/components/Icon";

export type SegmentedToggleProps<V extends string> = {
  options: ReadonlyArray<{ value: V; label: string; icon?: IconName }>;
  value: V;
  onChange: (v: V) => void;
  ariaLabel?: string;
};

export function SegmentedToggle<V extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: SegmentedToggleProps<V>) {
  return (
    <div className="seg" role="group" aria-label={ariaLabel ?? "View"}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={o.value === value ? "seg-btn on" : "seg-btn"}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.icon && <Icon name={o.icon} size={14} />}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}
